import { Collapse, CollapseProps, theme } from 'antd';
import styles from './index.module.less';
import { CSSProperties, useEffect, useMemo } from 'react';
import Link from 'next/link';
import { useConfigProvider } from '@/context/ConfigProvider';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { CommonBackContent } from '@/components/Layouts/CommonBackContent';
import { useUserProvider } from '@/context/UserProvider';

const FAQContent = () => {
  const { t } = useTranslation('common');
  const { token } = theme.useToken();
  const { isBlogWeb } = useConfigProvider();
  const router = useRouter();
  const { locale } = router;
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();

  const onSiginClick = () => {
    if (!isLogin) {
      onOpenLoginModal(true);
      onChangedLoginView(false);
    }
  };

  const dataList = [
    {
      label: `What is the Hashtag Generator Tool?`,
      children: `The Hashtag Generator Tool helps users create relevant hashtags for social media posts based on specific keywords or topics.`,
    },
    {
      label: `How does the tool generate hashtags?`,
      children: `The tool analyzes input keywords and provides a list of popular, related hashtags to increase the visibility of your content.`,
    },
    {
      label: `Can I customize the generated hashtags? `,
      children: `Yes, you can add your own hashtags or select from the suggested list. Our tool also includes a copy-paste function, allowing you to easily copy selected hashtags and paste them directly into your content.`,
    },
    {
      label: `Is the hashtag list based on current trends?`,
      children: `Yes, our tool uses up-to-date data to ensure the hashtags are relevant to current trends and popular topics.`,
    },
    {
      label: `Is the Hashtag Generator free to use?`,
      children: `Yes, the Hashtag Generator is completely free to use. You can access all available features without any cost.`,
    },
    {
      label: `How can I use hashtags to drive traffic to my Instagram account?`,
      children: `Using the right hashtags is key to expanding your content’s visibility and reaching users who are interested in your niche. Our free hashtag tool helps you discover the most relevant hashtags, increasing your chances of being found by users who are interested in similar content. By using these hashtags, your posts will appear in front of a larger audience, attracting them to your profile and potentially gaining new followers.`,
    },
    {
      label: `After gaining more followers, how can I track their activity, like what posts they’ve liked or what accounts they follow?`,
      children: (
        <p>
          Once you attract more users through hashtags, you might want to learn more about their interactions on
          Instagram—such as what posts they've liked or which accounts they follow. With our{' '}
          <Link style={{ color: 'blue' }} href="/">
            Instagram Activity Tracker
          </Link>
          , you can easily track these interactions and gain deeper insights into their social behavior. By using the
          tracker, you’ll be able to see what content resonates with your followers and get a better understanding of
          their interests and social circles, helping you tailor your own content strategy.
        </p>
      ),
    },
  ];

  const ptDataList = [
    {
      label: 'O que é a Ferramenta Geradora de Hashtags?',
      children:
        'A Ferramenta Geradora de Hashtags ajuda os usuários a criar hashtags relevantes para postagens em redes sociais com base em palavras-chave ou tópicos específicos.',
    },
    {
      label: 'Como a ferramenta gera hashtags?',
      children:
        'A ferramenta analisa as palavras-chave inseridas e fornece uma lista de hashtags populares e relacionadas para aumentar a visibilidade do seu conteúdo.',
    },
    {
      label: 'Posso personalizar as hashtags geradas?',
      children:
        'Sim, você pode adicionar suas próprias hashtags ou selecionar da lista sugerida. Nossa ferramenta também inclui uma função de copiar e colar, permitindo que você copie as hashtags selecionadas e cole-as diretamente no seu conteúdo.',
    },
    {
      label: 'A lista de hashtags é baseada nas tendências atuais?',
      children:
        'Sim, nossa ferramenta utiliza dados atualizados para garantir que as hashtags sejam relevantes para as tendências atuais e temas populares.',
    },
    {
      label: 'O Gerador de Hashtags é gratuito?',
      children:
        'Sim, o Gerador de Hashtags é totalmente gratuito. Você pode acessar todos os recursos disponíveis sem nenhum custo.',
    },
    {
      label: 'Como posso usar hashtags para direcionar tráfego para minha conta no Instagram?',
      children:
        'Usar as hashtags corretas é fundamental para aumentar a visibilidade do seu conteúdo e alcançar usuários que se interessam pelo seu nicho. Nossa ferramenta gratuita de hashtags ajuda você a descobrir as hashtags mais relevantes, aumentando suas chances de ser encontrado por usuários interessados em conteúdos semelhantes. Ao usar essas hashtags, suas postagens aparecerão para um público maior, atraindo novos seguidores em potencial.',
    },
    {
      label:
        'Depois de ganhar mais seguidores, como posso rastrear sua atividade, como quais postagens eles curtiram ou quais contas eles seguem?',
      children: (
        <p>
          Depois de atrair mais usuários por meio de hashtags, você pode querer saber mais sobre as interações deles no
          Instagram, como quais postagens curtiram ou quais contas seguem. Com nosso{' '}
          <Link style={{ color: 'blue' }} href="/">
            Rastreador de Atividades do Instagram
          </Link>
          , você pode facilmente rastrear essas interações e obter insights mais profundos sobre seu comportamento
          social. Usando o rastreador, você poderá ver quais conteúdos ressoam com seus seguidores e entender melhor
          seus interesses e círculos sociais, ajudando você a adaptar sua própria estratégia de conteúdo.
        </p>
      ),
    },
  ];

  const esDataList = [
    {
      label: '¿Qué es la Herramienta Generadora de Hashtags?',
      children:
        'La Herramienta Generadora de Hashtags ayuda a los usuarios a crear hashtags relevantes para publicaciones en redes sociales basados en palabras clave o temas específicos.',
    },
    {
      label: '¿Cómo genera hashtags la herramienta?',
      children:
        'La herramienta analiza las palabras clave ingresadas y proporciona una lista de hashtags populares y relacionados para aumentar la visibilidad de su contenido.',
    },
    {
      label: '¿Puedo personalizar los hashtags generados?',
      children:
        'Sí, puede agregar sus propios hashtags o seleccionar de la lista sugerida. Nuestra herramienta también incluye una función de copiar y pegar, lo que le permite copiar los hashtags seleccionados y pegarlos directamente en su contenido.',
    },
    {
      label: '¿La lista de hashtags se basa en las tendencias actuales?',
      children:
        'Sí, nuestra herramienta utiliza datos actualizados para asegurar que los hashtags sean relevantes para las tendencias actuales y temas populares.',
    },
    {
      label: '¿Es gratuito el Generador de Hashtags?',
      children:
        'Sí, el Generador de Hashtags es completamente gratuito. Puede acceder a todas las funciones sin ningún costo.',
    },
    {
      label: '¿Cómo puedo usar hashtags para atraer tráfico a mi cuenta de Instagram?',
      children:
        'Usar los hashtags correctos es clave para ampliar la visibilidad de su contenido y alcanzar a usuarios interesados en su nicho. Nuestra herramienta gratuita de hashtags le ayuda a descubrir los hashtags más relevantes, aumentando sus posibilidades de ser encontrado por usuarios interesados en contenido similar. Al utilizar estos hashtags, sus publicaciones aparecerán frente a un público más amplio, atrayendo potencialmente nuevos seguidores.',
    },
    {
      label:
        "Después de ganar más seguidores, ¿cómo puedo rastrear su actividad, como los posts que han dado 'me gusta' o las cuentas que siguen?",
      children: (
        <p>
          Una vez que atraiga a más usuarios a través de hashtags, podría querer conocer más sobre sus interacciones en
          Instagram—como los posts que han dado 'me gusta' o las cuentas que siguen. Con nuestro{' '}
          <Link style={{ color: 'blue' }} href="/">
            Rastreador de Actividad de Instagram
          </Link>
          , puede rastrear fácilmente estas interacciones y obtener una comprensión más profunda de su comportamiento
          social. Con el rastreador, podrá ver qué contenido resuena con sus seguidores y obtener una mejor comprensión
          de sus intereses y círculos sociales, ayudándole a adaptar su estrategia de contenido.
        </p>
      ),
    },
  ];

  const jaDataList = [
    {
      label: 'ハッシュタグ生成ツールとは何ですか？',
      children:
        'ハッシュタグ生成ツールは、特定のキーワードやテーマに基づいてソーシャルメディア投稿のための関連するハッシュタグを作成する手助けをします。',
    },
    {
      label: 'ツールはどのようにハッシュタグを生成しますか？',
      children:
        'ツールは入力されたキーワードを分析し、コンテンツの可視性を高めるために人気のある関連ハッシュタグのリストを提供します。',
    },
    {
      label: '生成されたハッシュタグをカスタマイズできますか？',
      children:
        'はい、自分でハッシュタグを追加したり、提案されたリストから選択することができます。ツールにはコピー＆ペースト機能もあり、選択したハッシュタグを簡単にコピーしてコンテンツに直接貼り付けることができます。',
    },
    {
      label: 'ハッシュタグリストは現在のトレンドに基づいていますか？',
      children:
        'はい、ツールは最新のデータを使用して、ハッシュタグが現在のトレンドや人気のあるトピックに関連していることを保証しています。',
    },
    {
      label: 'ハッシュタグ生成ツールは無料ですか？',
      children: 'はい、ハッシュタグ生成ツールは完全に無料です。すべての利用可能な機能を無料で利用できます。',
    },
    {
      label: 'ハッシュタグを使ってInstagramアカウントにトラフィックを増やす方法は？',
      children:
        '適切なハッシュタグを使用することは、コンテンツの視認性を高め、ニッチに興味を持つユーザーにリーチするために重要です。無料のハッシュタグツールは、最も関連性のあるハッシュタグを見つける手助けをし、似たようなコンテンツに興味があるユーザーに見つけられるチャンスを増やします。これらのハッシュタグを使用することで、投稿はより多くのオーディエンスの前に表示され、潜在的に新しいフォロワーを獲得することができます。',
    },
    {
      label:
        'フォロワーが増えた後、フォロワーのアクティビティ（例えば、どの投稿に「いいね」したかや、どのアカウントをフォローしているか）を追跡する方法は？',
      children: (
        <p>
          ハッシュタグを通じてユーザーを増やすと、Instagramでのフォロワーの行動（例えば、どの投稿に「いいね」したかや、どのアカウントをフォローしているか）を知りたくなるかもしれません。
          <Link style={{ color: 'blue' }} href="/">
            Instagramアクティビティトラッカー
          </Link>{' '}
          を使用することで、これらのインタラクションを簡単に追跡し、彼らの社会的行動に関するより深い洞察を得ることができます。このトラッカーを使うことで、どのコンテンツがフォロワーに響いているのかを確認し、フォロワーの興味やソーシャルサークルについての理解を深めることができ、コンテンツ戦略をより適切に調整する手助けとなります。
        </p>
      ),
    },
  ];

  const deDataList = [
    {
      label: 'Was ist das Hashtag-Generator-Tool?',
      children:
        'Das Hashtag-Generator-Tool hilft Nutzern, relevante Hashtags für Social-Media-Posts basierend auf bestimmten Schlüsselwörtern oder Themen zu erstellen.',
    },
    {
      label: 'Wie generiert das Tool Hashtags?',
      children:
        'Das Tool analysiert die eingegebenen Schlüsselwörter und liefert eine Liste beliebter, verwandter Hashtags, um die Sichtbarkeit Ihrer Inhalte zu erhöhen.',
    },
    {
      label: 'Kann ich die generierten Hashtags anpassen?',
      children:
        'Ja, Sie können Ihre eigenen Hashtags hinzufügen oder aus der vorgeschlagenen Liste auswählen. Unser Tool enthält auch eine Kopieren-Einfügen-Funktion, mit der Sie die ausgewählten Hashtags ganz einfach kopieren und direkt in Ihre Inhalte einfügen können.',
    },
    {
      label: 'Beruht die Hashtag-Liste auf aktuellen Trends?',
      children:
        'Ja, unser Tool verwendet aktuelle Daten, um sicherzustellen, dass die Hashtags für aktuelle Trends und beliebte Themen relevant sind.',
    },
    {
      label: 'Ist der Hashtag-Generator kostenlos?',
      children:
        'Ja, der Hashtag-Generator ist vollständig kostenlos. Sie können alle verfügbaren Funktionen ohne Kosten nutzen.',
    },
    {
      label: 'Wie kann ich Hashtags verwenden, um den Verkehr zu meinem Instagram-Konto zu lenken?',
      children:
        'Die richtigen Hashtags sind entscheidend, um die Sichtbarkeit Ihrer Inhalte zu erhöhen und Nutzer zu erreichen, die an Ihrer Nische interessiert sind. Unser kostenloses Hashtag-Tool hilft Ihnen, die relevantesten Hashtags zu finden und so Ihre Chancen zu erhöhen, von Nutzern gefunden zu werden, die sich für ähnliche Inhalte interessieren. Durch die Verwendung dieser Hashtags erscheinen Ihre Beiträge vor einem größeren Publikum und können neue Follower anziehen.',
    },
    {
      label:
        'Wie kann ich die Aktivitäten meiner Follower verfolgen, wie z.B. welche Beiträge sie geliked haben oder welchen Accounts sie folgen?',
      children: (
        <p>
          Sobald Sie durch Hashtags mehr Nutzer anziehen, möchten Sie vielleicht mehr über deren Interaktionen auf
          Instagram erfahren – wie z. B. welche Beiträge sie geliked haben oder welchen Accounts sie folgen. Mit unserem{' '}
          <Link style={{ color: 'blue' }} href="/">
            Instagram Activity Tracker
          </Link>{' '}
          können Sie diese Interaktionen einfach verfolgen und tiefere Einblicke in ihr soziales Verhalten gewinnen. Mit
          dem Tracker sehen Sie, welche Inhalte bei Ihren Followern Anklang finden und erhalten ein besseres Verständnis
          für ihre Interessen und sozialen Netzwerke, was Ihnen hilft, Ihre eigene Content-Strategie anzupassen.
        </p>
      ),
    },
  ];

  const itDataList = [
    {
      label: "Cos'è lo Strumento Generatore di Hashtag?",
      children:
        'Lo Strumento Generatore di Hashtag aiuta gli utenti a creare hashtag rilevanti per i post sui social media basati su parole chiave o argomenti specifici.',
    },
    {
      label: 'Come genera gli hashtag lo strumento?',
      children:
        'Lo strumento analizza le parole chiave inserite e fornisce un elenco di hashtag popolari e correlati per aumentare la visibilità dei tuoi contenuti.',
    },
    {
      label: 'Posso personalizzare gli hashtag generati?',
      children:
        'Sì, puoi aggiungere i tuoi hashtag o selezionare quelli dalla lista suggerita. Il nostro strumento include anche una funzione di copia e incolla, che ti consente di copiare facilmente gli hashtag selezionati e incollarli direttamente nei tuoi contenuti.',
    },
    {
      label: "L'elenco degli hashtag è basato sulle tendenze attuali?",
      children:
        'Sì, il nostro strumento utilizza dati aggiornati per garantire che gli hashtag siano rilevanti rispetto alle tendenze attuali e agli argomenti popolari.',
    },
    {
      label: 'Il Generatore di Hashtag è gratuito?',
      children:
        'Sì, il Generatore di Hashtag è completamente gratuito. Puoi accedere a tutte le funzionalità senza alcun costo.',
    },
    {
      label: 'Come posso usare gli hashtag per indirizzare il traffico al mio account Instagram?',
      children:
        'Usare i giusti hashtag è fondamentale per aumentare la visibilità dei tuoi contenuti e raggiungere utenti interessati alla tua nicchia. Il nostro strumento gratuito per gli hashtag ti aiuta a scoprire gli hashtag più rilevanti, aumentando le tue possibilità di essere trovato da utenti interessati a contenuti simili. Utilizzando questi hashtag, i tuoi post appariranno a un pubblico più ampio, attirando potenzialmente nuovi follower.',
    },
    {
      label:
        'Dopo aver guadagnato più follower, come posso monitorare la loro attività, come i post che hanno apprezzato o gli account che seguono?',
      children: (
        <p>
          Una volta che attiri più utenti attraverso gli hashtag, potresti voler saperne di più sulle loro interazioni
          su Instagram, come i post che hanno apprezzato o gli account che seguono. Con il nostro{' '}
          <Link style={{ color: 'blue' }} href="/">
            Instagram Activity Tracker
          </Link>
          , puoi facilmente monitorare queste interazioni e ottenere una comprensione più approfondita del loro
          comportamento sociale. Utilizzando il tracker, sarai in grado di vedere quali contenuti risuonano con i tuoi
          follower e ottenere una migliore comprensione dei loro interessi e cerchie sociali, aiutandoti a adattare la
          tua strategia di contenuto.
        </p>
      ),
    },
  ];

  const koDataList = [
    {
      label: '해시태그 생성기 도구란 무엇인가요?',
      children:
        '해시태그 생성기 도구는 특정 키워드나 주제를 기반으로 소셜 미디어 게시물에 적합한 해시태그를 생성하는 데 도움을 줍니다.',
    },
    {
      label: '도구는 어떻게 해시태그를 생성하나요?',
      children:
        '도구는 입력된 키워드를 분석하여 콘텐츠의 가시성을 높이기 위해 인기 있고 관련된 해시태그 목록을 제공합니다.',
    },
    {
      label: '생성된 해시태그를 사용자 지정할 수 있나요?',
      children:
        '네, 제안된 목록에서 선택하거나 직접 해시태그를 추가할 수 있습니다. 이 도구는 복사-붙여넣기 기능도 포함하고 있어 선택한 해시태그를 쉽게 복사하여 콘텐츠에 바로 붙여넣을 수 있습니다.',
    },
    {
      label: '해시태그 목록은 현재 트렌드에 기반한 것인가요?',
      children:
        '네, 저희 도구는 최신 데이터를 사용하여 해시태그가 현재 트렌드 및 인기 있는 주제와 관련이 있도록 보장합니다.',
    },
    {
      label: '해시태그 생성기는 무료인가요?',
      children: '네, 해시태그 생성기는 완전히 무료로 사용할 수 있습니다. 모든 기능을 비용 없이 이용할 수 있습니다.',
    },
    {
      label: '해시태그를 사용하여 내 인스타그램 계정으로 트래픽을 유도하려면 어떻게 해야 하나요?',
      children:
        '적절한 해시태그를 사용하는 것은 콘텐츠의 가시성을 높이고 관심 있는 사용자에게 도달하기 위해 매우 중요합니다. 저희의 무료 해시태그 도구는 가장 관련성 높은 해시태그를 찾아줌으로써, 유사한 콘텐츠에 관심이 있는 사용자가 귀하의 콘텐츠를 찾을 수 있는 가능성을 높여줍니다. 이러한 해시태그를 사용하면 게시물이 더 많은 관객에게 노출되어 새로운 팔로워를 유도할 수 있습니다.',
    },
    {
      label:
        '팔로워를 더 많이 확보한 후, 팔로워의 활동, 예를 들어 그들이 좋아한 게시물이나 팔로우하는 계정을 추적하는 방법은?',
      children: (
        <p>
          해시태그를 통해 더 많은 사용자를 유치한 후에는 인스타그램에서 팔로워가 좋아한 게시물이나 팔로우하는 계정을
          파악하고 싶어질 수 있습니다. 저희의{' '}
          <Link style={{ color: 'blue' }} href="/">
            Instagram Activity Tracker
          </Link>{' '}
          를 사용하면 이러한 상호작용을 쉽게 추적할 수 있으며, 팔로워의 소셜 행동에 대한 심층적인 통찰력을 얻을 수
          있습니다. 트래커를 사용하여 팔로워들이 선호하는 콘텐츠를 확인하고, 이들의 관심사와 소셜 서클을 더 잘 이해할 수
          있어 자신의 콘텐츠 전략을 보다 맞춤화할 수 있습니다.
        </p>
      ),
    },
  ];

  useEffect(() => {
    if (isBlogWeb) {
      router.push('/instagram-tracking-blog');
    }
  }, []);

  const panelStyle: React.CSSProperties = {
    marginBottom: 24,
    background: 'transparent',
    borderRadius: token.borderRadiusLG,
    border: 'none',
    color: 'red',
    boxShadow: `0 0 10px #E3E6F3`,
  };

  const getItems: (panelStyle: CSSProperties) => CollapseProps['items'] = (panelStyle) => {
    let tempList;
    if (locale === 'es') {
      tempList = esDataList;
    } else if (locale === 'pt') {
      tempList = ptDataList;
    } else if (locale === 'ja') {
      tempList = jaDataList;
    } else if (locale === 'de') {
      tempList = deDataList;
    } else if (locale === 'ko') {
      tempList = koDataList;
    } else if (locale === 'it') {
      tempList = itDataList;
    } else {
      tempList = dataList;
    }

    return tempList.map((item, index) => {
      return {
        label: item?.label,
        children:
          typeof item?.children === 'string' ? (
            <p dangerouslySetInnerHTML={{ __html: item?.children }} />
          ) : (
            item?.children
          ),
        key: `${index}`,
        style: panelStyle,
      };
    });
  };

  const getContent = useMemo(() => {
    return (
      <CommonBackContent style={{ background: 'transparent' }}>
        <div className={styles.content}>
          <div className={styles.topContainer}>
            <h2>{t("Frequently Asked Questions about Dolphin Radar's Hashtag Generator")}</h2>
          </div>

          <Collapse
            className={styles.collapseContent}
            bordered={false}
            items={getItems(panelStyle)}
            defaultActiveKey={undefined}
          />
        </div>
      </CommonBackContent>
    );
  }, [getItems]);

  if (isBlogWeb) {
    return <></>;
  }
  return <>{getContent}</>;
};

export default FAQContent;
